import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HardHat, Cpu, Phone, Mail, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { IMAGES } from "@/lib/media";

export default function Home() {
  const { t } = useI18n();

  return (
    <div data-testid="page-home">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-stone-500 font-mono mb-8">
              <span className="w-8 h-px bg-stone-400"></span>
              {t("hero.eyebrow")}
            </div>
            <h1 className="font-serif text-[44px] sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-stone-900">
              <span className="block">{t("hero.title.a")}</span>
              <span className="block italic text-[#B95736]">{t("hero.title.b")}</span>
              <span className="block">{t("hero.title.c")}</span>
            </h1>
            <p className="mt-8 text-lg text-stone-600 leading-relaxed max-w-xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary group">
                {t("hero.cta_primary")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="btn-outline group">
                {t("hero.cta_secondary")}
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-stone-200">
              <img
                src={IMAGES.hero_main}
                alt="GSM Service Maintenance"
                className="absolute inset-x-0 top-0 w-full h-[200%] object-cover"
                style={{ objectPosition: "center top" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/25 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-stone-200 bg-[#1C1917] py-4 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap gap-12 text-sm font-mono uppercase tracking-[0.2em] text-stone-400">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12">
                <span>{t("marquee")}</span>
                <span className="text-[#B95736]">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-serif text-5xl md:text-7xl text-stone-900 tracking-tight leading-[0.95]">
            {t("services.title")}
          </h2>
          <Link to="/services" data-testid="services-explore-all" className="link-arrow text-stone-900 self-start md:self-end">
            {t("services.learn_more")} <ArrowRight size={16} />
          </Link>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
          <ServiceCard
            testid="service-card-maintenance"
            to="/services/maintenance"
            className="md:col-span-4 md:row-span-2"
            image={IMAGES.service_maintenance}
            icon={<Sparkles size={18} />}
            tag={t("services.maintenance.tag")}
            title={t("services.maintenance.title")}
            large
          />
          <ServiceCard
            testid="service-card-postconstruction"
            to="/services/maintenance#details"
            className="md:col-span-2"
            image={IMAGES.service_postconstruction}
            icon={<HardHat size={18} />}
            tag={t("services.postconstruction.tag")}
            title={t("services.postconstruction.title")}
          />
          <ServiceCard
            testid="service-card-it"
            to="/services/informatique"
            className="md:col-span-2"
            image={IMAGES.service_it}
            icon={<Cpu size={18} />}
            tag={t("services.it.tag")}
            title={t("services.it.title")}
            slate
          />
        </div>
      </section>

      {/* ===== ABOUT PREVIEW ===== */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">{t("about.eyebrow")}</div>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl tracking-tight text-stone-900 leading-[1.05] max-w-3xl mx-auto">{t("about.title")}</h2>
          <div className="mt-8 space-y-5 max-w-2xl mx-auto">
            <p className="text-stone-600 text-lg leading-relaxed">{t("about.p1")}</p>
            <p className="text-stone-600 text-lg leading-relaxed">{t("about.p2")}</p>
          </div>
          <Link to="/about" data-testid="about-cta" className="mt-10 inline-flex link-arrow text-[#B95736]">
            {t("services.learn_more")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ===== CONTACT STRIP ===== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-2xl bg-[#1C1917] text-white p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.22em] uppercase text-stone-400 font-mono">{t("quote.eyebrow")}</div>
            <h3 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-tight">{t("quote.title")}</h3>
            <p className="mt-5 text-stone-300 text-lg max-w-lg">{t("quote.subtitle")}</p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-4">
            <Link to="/contact" data-testid="contact-strip-cta" className="btn-primary justify-center">
              {t("hero.cta_primary")} <ArrowRight size={16} />
            </Link>
            <a href="tel:+15146250359" data-testid="contact-strip-phone" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors">
              <Phone size={16} className="text-[#B95736]" /> 514-625-0359
            </a>
            <a href="mailto:gsmservicedemaintenance@gmail.com" data-testid="contact-strip-email" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors">
              <Mail size={16} className="text-[#B95736]" /> gsmservicedemaintenance@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ image, icon, tag, title, large, slate, className = "", testid, to = "/services" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-lg border border-stone-200 bg-white transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl block ${className}`}
    >
      <div className="absolute inset-0">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className={`absolute inset-0 ${slate ? "bg-gradient-to-t from-[#293845]/95 via-[#293845]/40 to-[#293845]/10" : "bg-gradient-to-t from-black/85 via-black/25 to-black/0"}`}></div>
      </div>
      <div className={`relative flex flex-col justify-end p-6 md:p-8 ${large ? "min-h-[420px] md:min-h-[560px]" : "min-h-[280px] md:min-h-[266px]"}`}>
        <div className="flex items-center justify-between gap-2 text-white/85 text-[10px] tracking-[0.22em] uppercase font-mono mb-3">
          <span className="flex items-center gap-2">
            <span className="p-1.5 rounded-full bg-white/10 backdrop-blur-sm">{icon}</span>
            {tag}
          </span>
          <ArrowRight size={16} className="text-white opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-transform" />
        </div>
        <h3 className={`font-serif text-white leading-tight ${large ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"}`}>{title}</h3>
      </div>
    </Link>
  );
}
